import React from 'react';
import { TemplateRenderProps } from './types';
import { FormFields, AddonsSelector, CouponSection, TrustAndSecurityBadge, SocialProofAndFomo } from './CheckoutCommonElements';
import { BillingCycleSelector, PlanFeatureList, TestimonialCard, PriceBreakdown } from './CheckoutSummaryElements';

export const GlassTemplate: React.FC<TemplateRenderProps> = ({
  plan,
  config,
  formData,
  onInputChange,
  billingCycle,
  onChangeCycle,
  selectedAddonIds,
  onToggleAddon,
  appliedCoupon,
  onApplyCoupon,
  onRemoveCoupon,
  onSubmitInfo,
  isCapturing,
  currencySymbol,
  addonsTotal,
  discountAmount,
  finalTotal,
}) => {
  return (
    <div
      className="min-h-full p-4 md:p-8 flex items-center justify-center relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4c1d95 100%)',
        color: '#f8fafc',
        fontFamily: config.fontFamily || 'Inter, sans-serif',
      }}
    >
      {/* Background ambient orbs */}
      <div className="absolute top-10 left-10 w-72 h-72 bg-pink-500/20 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-10 right-10 w-80 h-80 bg-indigo-500/20 rounded-full blur-3xl pointer-events-none"></div>

      <div className="w-full max-w-4xl backdrop-blur-xl bg-white/10 dark:bg-black/20 border border-white/20 rounded-3xl shadow-2xl p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-8 relative z-10">
        {/* Left Column */}
        <div className="md:col-span-5 flex flex-col justify-between space-y-6">
          <div className="space-y-4">
            {config.logoUrl && <img src={config.logoUrl} alt="Logo" className="h-6 brightness-0 invert object-contain" />}
            <div>
              <span className="text-[10px] uppercase font-black tracking-widest text-indigo-300">Selected Plan</span>
              <h2 className="text-2xl font-black text-white">{plan.tier}</h2>
              {plan.description && <p className="text-xs text-slate-300 mt-1">{plan.description}</p>}
            </div>

            <BillingCycleSelector billingCycle={billingCycle} onChangeCycle={onChangeCycle} config={config} />
            <PlanFeatureList features={plan.features} textColorClass="text-slate-200" checkColorClass="text-pink-400" />
            {config.showTestimonial && <TestimonialCard testimonial={config.testimonial} />}
          </div>

          <div className="p-4 rounded-2xl bg-white/5 border border-white/10">
            <div className="flex justify-between items-baseline">
              <span className="text-xs font-bold text-slate-300">Total Recurring</span>
              <span className="text-2xl font-black text-white">{currencySymbol}{finalTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Right Column Form */}
        <div className="md:col-span-7 backdrop-blur-md bg-white/10 rounded-2xl p-6 border border-white/20 space-y-5">
          <div>
            <h1 className="text-lg font-bold text-white">{config.headerMain || 'Billing Details'}</h1>
            <p className="text-xs text-slate-300">{config.headerSub || 'Complete your setup with instant confirmation.'}</p>
          </div>

          <SocialProofAndFomo config={config} />

          <form onSubmit={onSubmitInfo} className="space-y-4">
            <FormFields
              config={config}
              formData={formData}
              onInputChange={onInputChange}
              inputClassName="bg-white/15 border-white/20 text-white placeholder:text-slate-300 focus:bg-white/25 focus:border-pink-400"
              labelClassName="text-slate-200 font-bold text-[11px]"
            />
            <AddonsSelector addons={config.addons} selectedAddonIds={selectedAddonIds} onToggleAddon={onToggleAddon} currency={plan.currency} />
            <CouponSection config={config} appliedCoupon={appliedCoupon} onApplyCoupon={onApplyCoupon} onRemoveCoupon={onRemoveCoupon} />

            <button
              type="submit"
              disabled={isCapturing}
              className="w-full py-3.5 text-xs font-black uppercase tracking-wider text-white shadow-xl transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50"
              style={{
                background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
                borderRadius: config.buttonRadius || '12px',
              }}
            >
              {isCapturing ? 'Processing...' : config.buttonText || 'Unlock Access Now'}
            </button>
          </form>

          <TrustAndSecurityBadge config={config} />
        </div>
      </div>
    </div>
  );
};
